<template>
  <div class="add-to-cart-modal">
    <fn-modal
      v-if="entry"
      :headline-text="$t('cart.cartAddition.modal.headline')"
      headline-icon="circle-tick"
      headline-color="green"
      max-width="xl"
      class="atc-layer"
      @close="$emit('close')"
    >
      <fn-grid class="separator">
        <fn-grid-item
          columns="12"
          columns-m="8"
          class="modal-text"
        >
          <fn-paragraph>
            <strong>{{ entry.product.brandName }}</strong>
            {{ entry.product.name }}
          </fn-paragraph>

          <fn-paragraph>
            <span
              v-for="selectedVariant in entry.product.selectedVariant"
              :key="selectedVariant.title"
              class="ce-selected-variant"
            >
              {{ selectedVariant.title }}: <span class="value">{{ selectedVariant.value }}</span>
            </span>
          </fn-paragraph>

          <fn-paragraph class="shipping-info">
            <span class="shipping-time">
              <fn-icon icon="truck" />
              {{
                $t('shipping.status.default', {
                  min: entry.product.shippingInformation.deliveryTimeMin,
                  max: entry.product.shippingInformation.deliveryTimeMax,
                })
              }}
            </span>
            <span
              v-if="getShowFreeShipping && !isSpeditionOnBasestoreCH"
              class="shipping-text"
            >
              <fn-icon
                icon="check-bold"
                color="green"
              />
              {{ $t('shipping.freeShippingText') }}
              <template v-if="shippingFee">
                {{
                  $t('shipping.freeShippingTreshholdText', {
                    freeShipping: entry.product.shippingInformation.freeShippingThreshold.formattedValue,
                  })
                }}
              </template>
            </span>
          </fn-paragraph>
        </fn-grid-item>

        <fn-grid-item
          columns="12"
          columns-m="4"
          class="button-area"
        >
          <fn-grid class="button-group reversible">
            <fn-grid-item columns="12">
              <fn-button
                quaternary
                full-width
                @click="$emit('close')"
              >
                {{ $t('cart.buttons.continueShopping') }}
              </fn-button>
            </fn-grid-item>
            <fn-grid-item columns="12">
              <fn-button
                secondary
                full-width
                icon="cart-new"
                @click="handleCta"
              >
                <span class="button-text">
                  {{ $t('cart.buttons.goToCart') }}
                </span>
              </fn-button>
            </fn-grid-item>
          </fn-grid>
        </fn-grid-item>
      </fn-grid>

      <fn-grid class="reco-slot">
        <fn-grid-item columns="12">
          <fn-adobe-target-mbox-wrapper>
            <fn-adobe-target-mbox
              id="recoSlot_atcLayer"
              placement="atc_layer_recommendation"
            />
          </fn-adobe-target-mbox-wrapper>
        </fn-grid-item>
      </fn-grid>
    </fn-modal>
  </div>
</template>

<script>
import { mapState } from 'pinia';

export default {
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    buttonText: {
      type: String,
      default: '',
    },
    entry: {
      required: true,
      type: Object,
    },
    addedQuantity: {
      required: true,
      type: Number,
    },
  },
  emits: ['close'],
  computed: {
    ...mapState(useMainStore, {
      baseStore: 'baseStore',
      getShowFreeShipping: 'getShowFreeShipping',
    }),
    shippingFee() {
      return (
        this.entry?.product?.shippingInformation?.shippingFee &&
        this.entry.product.shippingInformation.shippingFee.value > 0
      );
    },
    isSpeditionOnBasestoreCH() {
      return (
        this.baseStore === useRuntimeConfig().public.baseStoreCh &&
        this.entry?.product?.shippingInformation?.detailedShippingInformation === 'SPEDITION'
      );
    },
  },
  methods: {
    handleCta() {
      this.$emit('close');
      this.$router.push(this.$t('url.cart'));
    },
  },
};
</script>

<style lang="scss" scoped>
.add-to-cart-modal {
  .modal {
    max-width: 810px;

    :deep(.button-group .grid) {
      flex: 0 1 auto;
      margin: 0;
      justify-content: space-between;

      .grid-item {
        display: flex;
        margin-right: 0;
        margin-left: 0;
        justify-content: flex-end;
      }
    }

    @include bp(l) {
      :deep(.modal-text),
      :deep(.button-area.grid-item) {
        margin-bottom: 0;
      }
    }

    :deep(.adobe-cart-reco-slider) {
      margin-bottom: 0;
    }

    :deep(.button-group .grid .grid-item) {
      width: 100%;
      max-width: 100%;
    }

    :deep(.heading .icon) {
      top: -3px;
    }

    @include bp(m, max) {
      :deep(.headline-wrapper) {
        position: static;
      }
    }

    :deep(div.m-actions) {
      border-bottom: none;
      position: relative;
    }

    :deep(.m-actions .icon) {
      color: palette(grey, base);
      font-size: 20px;
    }

    :deep(.m-header) {
      @include bp(m, max) {
        padding: $pad $pad 0;
      }
    }

    :deep(.m-content) {
      padding: $pad $pad * 2;

      @include bp(m, max) {
        padding: $pad $pad math.div($pad, 2);
      }
    }
  }

  :deep(.product-teaser) {
    @include bp(m, max) {
      .pt-head {
        max-height: 40px;
      }

      .p-price-box {
        flex-direction: column;
      }

      .pt-variant-info {
        display: none;
      }

      &.product-teaser--atc-layer {
        .pt-footer {
          align-items: flex-end;
          flex-direction: row-reverse;
          padding-top: $pad;
        }
      }
    }

    &:hover {
      box-shadow: none;
    }
  }

  .modal-text {
    font-size: map-get($font-sizes, 'l');

    .ce-selected-variant {
      margin-right: math.div($space, 4);
    }

    p {
      margin: 0;

      &.shipping-info {
        margin-top: math.div($space, 4);
        font-size: 13px;
        font-weight: bold;
        color: palette(grey, base);
        .icon {
          margin-right: math.div($space, 12);
        }
      }
    }

    strong {
      margin-right: math.div($space, 12);
    }

    .shipping-time {
      margin-left: math.div($space, 12);
    }

    .shipping-text {
      margin-left: math.div($space, 4);
    }
  }

  .ce-selected-variant {
    font-weight: bold;
    color: palette(grey, base);
  }

  .icon--circle-tick {
    margin-left: math.div($space, 4);
  }

  .grid-container.reco-slot {
    margin-right: math.div($space, 3);
    @include bp(m) {
      margin-top: math.div($space, 2);
    }
  }

  .separator {
    border-top: 1px solid palette(grey, light);
    @include bp(m) {
      padding-top: $pad;
    }
  }

  @include bp(m, max) {
    button.button {
      width: 100%;
      margin: 0;
    }

    .modal-text {
      padding-left: 0;
      padding-top: $pad;
    }

    h2,
    h3 {
      margin: 0 math.div($space, 2) math.div($space, 2);
    }

    p {
      margin: 0 math.div($space, 2) 0 0;
    }

    button {
      width: auto;
    }

    .single-item.price {
      font-size: map-get($font-sizes, 'm');
    }

    .shipping-time,
    .shipping-text {
      display: none;
    }

    :deep(.button-group .grid) {
      flex-direction: column-reverse;
    }

    :deep(.button.button--secondary .icon) {
      position: relative;
      top: -3px;
      left: -24px;
      margin: 0 (-$space * 0.8);
      line-height: 21px;
    }
  }
}
</style>
